import dayjs from 'dayjs';
import { OnboardingStage, VerificationStatus } from '@/types';
import { INDIA_COUNTRY_CODE, fromE164India } from '@/shared/utils/phone';
import type { AdminOnboardingItem } from '@/features/admin/api/admin.api';

/**
 * The CSV an admin uploads to SpringVerify's bulk-candidate screen.
 *
 * There is no SpringVerify API — the whole background check is manual — so this
 * file is the handover. Everything SpringVerify-shaped lives in this module:
 * when the real template turns out to want different headers, this is the only
 * file that changes.
 */

/**
 * Header row. Best-effort match for SpringVerify's bulk template — verify
 * against the real one before the first upload.
 */
export const SPRINGVERIFY_COLUMNS = [
  'Candidate First Name',
  'Candidate Last Name',
  'Email',
  'Country Code',
  'Mobile Number',
  'City',
  'State',
  'Profile ID',
  'Applied On',
] as const;

/**
 * The applicant's display name, with the same precedence the table uses.
 *
 * Note the `||` rather than `??`: `[].filter(Boolean).join(' ')` is `''`, not
 * undefined, so `??` would hand back an empty string and never reach the
 * fallback.
 */
export function onboardingRowName(row: AdminOnboardingItem): string {
  const fromUser = [row.user?.firstName, row.user?.lastName].filter(Boolean).join(' ');
  return row.fullName?.trim() || fromUser || 'Unnamed';
}

/**
 * Applicants whose details still have to go to the vendor.
 *
 * PENDING only. IN_PROGRESS is already with SpringVerify, and re-exporting is
 * the double-submission this export exists to prevent; NOT_STARTED is a legacy
 * record the state machine will not let an admin advance at all, so including
 * it would fail every batch.
 */
export function isAwaitingSpringVerifySend(row: AdminOnboardingItem): boolean {
  return (
    row.stage === OnboardingStage.BACKGROUND_CHECK &&
    row.lifecycle.verificationStatus === VerificationStatus.PENDING
  );
}

/** Rows SpringVerify will reject on upload, so the admin hears about them first. */
export function isMissingContactDetails(row: AdminOnboardingItem): boolean {
  return !row.user?.email || !row.user?.phone;
}

/**
 * The 10-digit national number, or null when this is not an Indian mobile.
 *
 * `fromE164India` alone is not enough: it takes the last ten digits of whatever
 * it is given, so a US `+1 415 555 0100` comes back as `4155550100` — ten
 * plausible-looking digits with the country code quietly removed. We only trust
 * it when the stored value actually is an Indian number.
 */
function toIndianNationalMobile(phone?: string): string | null {
  if (!phone) return null;

  const digits = phone.replace(/\D/g, '');
  const isIndian =
    digits.length === 10 || (digits.length === 12 && digits.startsWith('91'));
  if (!isIndian) return null;

  const national = fromE164India(phone);
  // Indian mobile numbers start 6-9; anything else is a landline or garbage.
  return /^[6-9]\d{9}$/.test(national) ? national : null;
}

/** One CSV record, in `SPRINGVERIFY_COLUMNS` order. */
export function toSpringVerifyRow(row: AdminOnboardingItem): string[] {
  const [first, ...rest] = onboardingRowName(row).split(/\s+/);
  const national = toIndianNationalMobile(row.user?.phone);

  return [
    first ?? '',
    // SpringVerify rejects a blank surname; a dot is the conventional filler.
    rest.join(' ') || '.',
    row.user?.email ?? '',
    INDIA_COUNTRY_CODE,
    // Anything we cannot read as an Indian mobile goes out verbatim rather than
    // silently truncated, so the admin can see what is wrong with it.
    national ?? row.user?.phone ?? '',
    row.city ?? '',
    row.state ?? '',
    // The reconciliation key: it is how a returned verdict maps back to a row.
    row.id,
    dayjs(row.createdAt).format('YYYY-MM-DD'),
  ];
}

/** A phone the vendor's template will not accept as an Indian mobile. */
export function hasUnusablePhone(row: AdminOnboardingItem): boolean {
  return Boolean(row.user?.phone) && toIndianNationalMobile(row.user?.phone) === null;
}

export function springVerifyFilename(now = new Date()): string {
  return `springverify-batch-${dayjs(now).format('YYYY-MM-DD-HHmm')}.csv`;
}
